import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const VerifyPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink, refreshBusiness } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verify = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid verification link');
        return;
      }

      try {
        const response = await verifyMagicLink(token);
        setStatus('success');
        toast.success('Successfully signed in!');
        
        // Check if user has a business
        const business = await refreshBusiness();
        
        setTimeout(() => {
          if (response.user.role === 'staff') {
            navigate('/staff');
          } else if (business) {
            navigate('/dashboard');
          } else {
            navigate('/setup');
          }
        }, 1500);
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setErrorMessage(error.response?.data?.detail || 'Failed to verify magic link');
      }
    };

    verify();
  }, [searchParams, verifyMagicLink, navigate, refreshBusiness]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="max-w-md w-full text-center">
        {status === 'verifying' && (
          <div className="animate-fade-in">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold mb-2">Verifying your link...</h1>
            <p className="text-muted-foreground">Please wait while we sign you in.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-scale-in">
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-accent" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">You're signed in!</h1>
            <p className="text-muted-foreground mb-6">Redirecting to your dashboard...</p>
            <div className="flex items-center justify-center gap-1">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" style={{ animationDelay: '0.2s' }} />
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="animate-fade-in">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Verification Failed</h1>
            <p className="text-muted-foreground mb-6">{errorMessage}</p>
            <Link to="/auth/login">
              <Button data-testid="back-to-login-btn">
                Back to Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyPage;
